<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-select
          v-model="form.year_batch"
          :items="examResultBatches"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Batch"
        ></v-select>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.programme"
          :items="examResultProgrammes"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Programme"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-text-field
          v-model.trim="form.usn_id"
          label="USN"
          outlined
          dense
          hide-details="auto"
          clearable
        />
      </v-col>
      <v-col cols="12" md="2" class="py-0 my-0">
        <v-autocomplete
          v-model="form.result"
          :items="results"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Result"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="getStudentExamResults(true)"
          :disabled="loading"
        >
          Search
        </v-btn>
        <v-btn
          :disabled="!examResults.length"
          class="accent white--text ml-2"
          :loading="loading"
          @click="download()"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
        <v-btn class="accent white--text ml-2" @click="dialog = true">
          <v-icon>mdi-upload</v-icon> Upload
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :items="examResults"
            :server-items-length="totalRecords"
            :options.sync="tableOptions"
            :footer-props="{ 'items-per-page-options': [10, 50, 100] }"
            fixed-header
            class="secondary row-height-50"
            :loading="loading"
            @update:options="onTableOptionsChange"
          >
            <template v-slot:item.internal_mark="{ item }">
              {{ item.internal_mark ?? "-" }}
            </template>
            <template v-slot:item.credit="{ item }">
              {{ item.credit ?? "-" }}
            </template>
            <template v-slot:item.max_internal="{ item }">
              {{ item.max_internal ?? "-" }}
            </template>
            <template v-slot:item.total_mark="{ item }">
              {{ item.total_mark ?? "-" }}
            </template>
            <template v-slot:item.usn_id="{ item }">
              <span
                class="usn-link primary--text"
                @click="onUSNClick(item.usn_id)"
              >
                {{ item.usn_id }}
              </span>
            </template>
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>
    <v-dialog v-model="dialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="font-weight-medium secondary--text primary">
          Upload Annual Exam Result
          <v-spacer></v-spacer>
          <v-btn small icon @click="(selectedFile = null), (dialog = false)">
            <v-icon small color="secondary">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-5">
          <v-row>
            <v-col cols="12">
              <v-file-input
                v-model="selectedFile"
                outlined
                dense
                accept=".csv"
                hide-details="auto"
                prepend-inner-icon="mdi-file-upload"
                prepend-icon=""
                label="Upload CSV"
              >
                <template v-slot:selection="{ text }">
                  <span
                    class="text-body-2 text-truncate"
                    style="max-width: 100%"
                  >
                    {{ selectedFile ? selectedFile.name : text }}
                  </span>
                </template>
              </v-file-input>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pt-0 mx-2">
          <v-spacer></v-spacer>
          <v-btn
            class="primary"
            :loading="loading"
            @click="uploadStudentExamResult"
          >
            Save</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { studentAnnualExamResultHeaders } from "@/constants/headers";

export default {
  components: {},
  data() {
    return {
      form: {},
      loading: false,
      batches: [],
      examResultProgrammes: [],
      examResultBatches: [],
      examResults: [],
      headers: studentAnnualExamResultHeaders,
      results: ["PASS", "ABSENT", "FAIL"],
      dialog: false,
      selectedFile: null,
      totalRecords: 0,
      tableOptions: {
        page: 1,
        itemsPerPage: 10,
      },
      isResetting: false,
    };
  },
  watch: {},
  computed: {},
  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Annual Exam Result" }]);

    this.init();
  },
  methods: {
    onUSNClick(usn) {
      this.form.usn_id = usn;
      this.getStudentExamResults(true);
    },
    objectToFilteredObject(obj) {
      const newObj = { ...obj };
      if (newObj.year_batch) {
        const [year, month] = newObj.year_batch.split("-");
        newObj.exam_year = year;
        newObj.exam_month = month;
        delete newObj.year_batch;
      }
      return Object.keys(newObj)
        .filter(
          (key) =>
            newObj[key] !== undefined &&
            newObj[key] !== null &&
            newObj[key] !== "",
        )
        .reduce((acc, key) => {
          acc[key] = newObj[key];
          return acc;
        }, {});
    },
    async init() {
      this.generateStudentProgrammes();
      this.generateExamBatchList();
    },
    onTableOptionsChange(options) {
      if (this.isResetting) return;
      this.tableOptions = options;
      this.getStudentExamResults();
    },
    async getStudentExamResults(resetPage = false) {
      if (resetPage) {
        this.isResetting = true;
        this.tableOptions = { ...this.tableOptions, page: 1 };
        this.$nextTick(() => {
          this.isResetting = false;
        });
      }

      this.loading = true;
      const form = this.objectToFilteredObject({ ...this.form });
      try {
        const res = await this.$axios.get("/annual_exam_sessions", {
          params: {
            ...form,
            page: this.tableOptions.page,
            per_page: this.tableOptions.itemsPerPage,
          },
        });
        this.examResults = res.data.data.records;
        this.totalRecords = res.data.data.total;
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async generateStudentProgrammes() {
      try {
        const response = await this.$axios.get(
          "/annual_exam_session_programmes",
        );
        this.examResultProgrammes = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async generateExamBatchList() {
      try {
        const response = await this.$axios.get("/annual_exam_session_batches");
        this.examResultBatches = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async download() {
      const form = this.objectToFilteredObject({
        ...this.form,
      });
      this.$axios
        .get(`/download_annual_exam_results`, {
          responseType: "blob",
          params: { ...form },
        })
        .then((res) => {
          let blob = new Blob([res.data], { type: "text/csv" }),
            url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", "Annual Exam Results.csv");
          document.body.appendChild(link);
          link.click();
        });
    },
    async uploadStudentExamResult() {
      this.loading = true;
      const formData = new FormData();

      formData.append("csv_file", this.selectedFile, this.selectedFilename);
      await this.$axios
        .post(`/upload_annual_exam_sessions`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((response) => {
          this.loading = false;

          if (response.status === 201) {
            // Create a URL for the CSV file blob
            const url = window.URL.createObjectURL(new Blob([response.data]));

            // Create a temporary <a> element for downloading the file
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `Annual Exam Result Errors.csv`); // File name
            document.body.appendChild(link);
            link.click(); // Trigger the download
            document.body.removeChild(link);
            this.selectedFile = null;
            this.dialog = false;
            return;
          }
          this.$store.commit("showSnackbar", {
            message: "Uploaded Successfully",
            color: "success",
          });
          this.selectedFile = null;
          this.dialog = false;
          this.getStudentExamResults();
        })
        .catch(async (error) => {
          this.loading = false;
          const errorMessage = await this.$store.dispatch(
            "throwError",
            error.response,
          );
          if (errorMessage == "Uploaded csv has invalid headers")
            this.selectedFile = null;
          this.$store.commit("showSnackbar", {
            message: errorMessage,
            color: "error",
          });
        });
    },
  },
};
</script>

<style scoped>
.usn-link {
  text-decoration: underline;
  cursor: pointer;
}

.usn-link:hover {
  opacity: 0.8;
}
</style>
